/**
 * 后处理任务卡片 — 前后对比布局
 */
import React, { useRef, useState } from 'react';
import { Loader2, AlertCircle, Download, RefreshCw, Trash2, Play, Pause, Maximize2, X, Wand2, Ban } from 'lucide-react';

const SCENE_LABELS = { aigc: 'AIGC', short_series: '短剧', ugc: 'UGC', old_film: '老片修复' };
const VERSION_LABELS = { standard: '标准版', professional: '专业版' };

const VideoLightbox = ({ url, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'var(--overlay-heavy)' }} onClick={onClose}>
      <div className="relative mx-4" style={{ maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
        <video src={url} className="rounded-xl" style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', backgroundColor: '#000' }} controls autoPlay loop />
        <button onClick={onClose} className="absolute -top-3 -right-3 w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: 'var(--overlay-medium)' }}>
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const MiniPlayer = ({ url, theme }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  const toggle = () => {
    if (!ref.current) return;
    if (playing) ref.current.pause(); else ref.current.play();
  };

  return (
    <>
      {lightbox && <VideoLightbox url={url} onClose={() => setLightbox(false)} />}
      <div className="relative w-full aspect-video rounded-xl overflow-hidden group cursor-pointer" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
        <video ref={ref} src={url} className="w-full h-full object-contain"
          onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={toggle}>
          <div className="w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm" style={{ backgroundColor: 'var(--overlay-medium)' }}>
            {playing ? <Pause size={16} className="text-white" /> : <Play size={16} className="text-white ml-0.5" />}
          </div>
        </div>
        <button className="absolute bottom-1.5 right-1.5 p-1 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: 'var(--overlay-medium)' }} onClick={() => setLightbox(true)}>
          <Maximize2 size={10} />
        </button>
      </div>
    </>
  );
};

const PostProcessCard = ({ task, theme, onDelete, onRetry }) => {
  const isEnhance = task.type === 'enhance';
  const typeColor = isEnhance ? 'var(--color-purple-primary)' : 'var(--color-error)';
  const typeColorSoft = isEnhance ? 'color-mix(in srgb, var(--color-purple-primary) 13%, transparent)' : 'color-mix(in srgb, var(--color-error) 13%, transparent)';
  const typeLabel = isEnhance ? '画质增强' : '去字幕';
  const typeIcon = isEnhance ? <Wand2 size={10} /> : <Ban size={10} />;

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  return (
    <div className="va-card group rounded-2xl overflow-hidden" style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border-secondary)' }}>
      {/* 顶部：类型标签 + 参数 + 时间 */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        <span className="text-2xs px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1" style={{ backgroundColor: typeColorSoft, color: typeColor }}>
          {typeIcon} {typeLabel}
        </span>
        {isEnhance && task.config && (
          <>
            <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
              {SCENE_LABELS[task.config.scene] || task.config.scene}
            </span>
            <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
              {VERSION_LABELS[task.config.toolVersion] || task.config.toolVersion}
            </span>
            <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
              {task.config.resolution}
            </span>
            {task.config.fps && (
              <span className="text-2xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
                {task.config.fps}fps
              </span>
            )}
          </>
        )}
        <span className="text-2xs ml-auto" style={{ color: 'var(--color-text-muted)' }}>{formatTime(task.createdAt)}</span>
      </div>

      {/* 中间：前后对比 */}
      <div className="px-4 pb-3">
        <div className="flex gap-3 items-stretch">
          {/* 原视频 */}
          <div className="flex-1 min-w-0">
            <div className="text-2xs font-bold mb-1.5" style={{ color: 'var(--color-text-muted)' }}>原视频</div>
            {task.sourceVideoUrl ? (
              <MiniPlayer url={task.sourceVideoUrl} theme={theme} />
            ) : (
              <div className="w-full aspect-video rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>无</span>
              </div>
            )}
          </div>

          {/* 箭头 */}
          <div className="flex items-center px-1 pt-5">
            <span className="text-lg" style={{ color: 'var(--color-text-muted)', opacity: 0.4 }}>→</span>
          </div>

          {/* 结果 */}
          <div className="flex-1 min-w-0">
            <div className="text-2xs font-bold mb-1.5" style={{ color: typeColor }}>{typeLabel}后</div>
            {task.status === 'success' && task.resultVideoUrl ? (
              <MiniPlayer url={task.resultVideoUrl} theme={theme} />
            ) : task.status === 'processing' ? (
              <div className="w-full aspect-video rounded-xl flex flex-col items-center justify-center gap-2" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                <Loader2 size={20} className="animate-spin" style={{ color: typeColor }} />
                <span className="text-2xs animate-pulse" style={{ color: typeColor }}>处理中...</span>
              </div>
            ) : task.status === 'failed' ? (
              <div className="w-full aspect-video rounded-xl flex flex-col items-center justify-center gap-2" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                <AlertCircle size={20} style={{ color: theme.colors.error }} />
                <span className="text-2xs text-center px-2" style={{ color: theme.colors.error }}>{task.error || '处理失败'}</span>
              </div>
            ) : (
              <div className="w-full aspect-video rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>等待中</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 底部操作栏 */}
      <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
        <div className="flex items-center gap-1">
          {task.resultVideoUrl && (
            <a href={task.resultVideoUrl} download className="p-1.5 rounded-lg transition-all hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-muted)' }} title="下载结果">
              <Download size={14} />
            </a>
          )}
          {task.type === 'erase-subtitle' && task.status === 'failed' && onRetry && (
            <button onClick={() => onRetry(task)} className="p-1.5 rounded-lg transition-all hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-muted)' }} title="重试">
              <RefreshCw size={14} />
            </button>
          )}
        </div>
        <button onClick={() => onDelete?.(task)} className="p-1.5 rounded-lg transition-all opacity-0 group-hover:opacity-100 hover:bg-[var(--color-red-light)]" style={{ color: theme.colors.error }} title="删除">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

export default PostProcessCard;
